import { Injectable, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { BehaviorSubject } from 'rxjs';

export interface MobileDetailViewHeaderState {
  compact: boolean;
  scrollingDown: boolean;
  mobile: boolean;
}

export interface MobileDetailViewShellState {
  active: boolean;
  hideInnerNavToggle: boolean;
  hideMobileToolbar: boolean;
}

export const DEFAULT_MOBILE_DETAIL_VIEW_SHELL_STATE: MobileDetailViewShellState = {
  active: false,
  hideInnerNavToggle: false,
  hideMobileToolbar: false,
};

@Injectable({
  providedIn: 'root',
})
export class MobileDetailViewShellService {
  private readonly router = inject(Router);
  private readonly headerStates = new Map<number, MobileDetailViewHeaderState>();
  private readonly stateSubject = new BehaviorSubject<MobileDetailViewShellState>(
    DEFAULT_MOBILE_DETAIL_VIEW_SHELL_STATE
  );
  private nextHeaderId = 0;
  private routeHidesInnerNav = this.urlHidesInnerNav(this.router.url);

  readonly state$ = this.stateSubject.asObservable();

  createHeaderId(): number {
    return this.nextHeaderId++;
  }

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.routeHidesInnerNav = this.urlHidesInnerNav(event.url);
        this.emitState();
        return;
      }

      if (event instanceof NavigationEnd) {
        this.routeHidesInnerNav = this.urlHidesInnerNav(event.urlAfterRedirects);
        this.emitState();
        return;
      }

      if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.routeHidesInnerNav = this.urlHidesInnerNav(this.router.url);
        this.emitState();
      }
    });
  }

  register(headerId: number, state: MobileDetailViewHeaderState): void {
    this.headerStates.set(headerId, state);
    this.emitState();
  }

  update(headerId: number, state: MobileDetailViewHeaderState): void {
    const currentState = this.headerStates.get(headerId);
    if (!currentState || this.headerStatesEqual(currentState, state)) {
      return;
    }
    this.headerStates.set(headerId, state);
    this.emitState();
  }

  unregister(headerId: number): void {
    this.headerStates.delete(headerId);
    this.emitState();
  }

  private emitState(): void {
    const headers = Array.from(this.headerStates.values());
    const anyMobileHeader = headers.some((header) => header.mobile);
    // Keep the mobile structure (submenu) toggle on admin settings/form pages that
    // register a detail actions bar. Only hide it on routes like /perform.
    const nextState: MobileDetailViewShellState = {
      active: anyMobileHeader,
      hideInnerNavToggle: this.routeHidesInnerNav,
      hideMobileToolbar: headers.some((header) => header.compact && header.scrollingDown),
    };

    if (this.shellStatesEqual(this.stateSubject.value, nextState)) {
      return;
    }

    this.stateSubject.next(nextState);
  }

  private headerStatesEqual(
    currentState: MobileDetailViewHeaderState,
    nextState: MobileDetailViewHeaderState
  ): boolean {
    return (
      currentState.compact === nextState.compact &&
      currentState.mobile === nextState.mobile &&
      currentState.scrollingDown === nextState.scrollingDown
    );
  }

  private shellStatesEqual(
    currentState: MobileDetailViewShellState,
    nextState: MobileDetailViewShellState
  ): boolean {
    return (
      currentState.active === nextState.active &&
      currentState.hideInnerNavToggle === nextState.hideInnerNavToggle &&
      currentState.hideMobileToolbar === nextState.hideMobileToolbar
    );
  }

  private urlHidesInnerNav(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return /\/perform(?:\/|$)/.test(path);
  }
}
